import { Box, Chip, Typography } from '@mui/material'
import React from 'react'

import Database from '../../types/Database'
import { generateColorFromString } from '../../utils/FormatHelpers'

interface BookingsSectionProps {
  bookings: Database.Booking[]
}

/**
 * BookingsSection - Displays bookings for a calendar day
 *
 * Shows booking chips with user names and desk information.
 * Limits display to 3 bookings with expand option for more.
 */
export default function BookingsSection({ bookings }: BookingsSectionProps) {
  const [expanded, setExpanded] = React.useState(false)
  const MAX_VISIBLE = 3

  if (bookings.length === 0) {
    return (
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{
          fontSize: '0.65rem',
          textAlign: 'center',
          display: 'block',
          py: 0.25,
          opacity: 0.6
        }}
      >
        —
      </Typography>
    )
  }

  // Show all if 4 or less, otherwise show 3 with expand option
  const shouldTruncate = bookings.length > 4
  const visibleBookings =
    expanded || !shouldTruncate ? bookings : bookings.slice(0, MAX_VISIBLE)
  const remainingCount = bookings.length - MAX_VISIBLE

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.25,
        justifyContent: 'flex-start'
      }}
    >
      {visibleBookings.map(booking => {
        const officeColor = generateColorFromString(booking.officeName ?? '')
        return (
          <Box
            key={booking.bookingKey}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: 'transparent',
              border: '1px solid',
              borderColor: officeColor,
              fontSize: '0.65rem',
              fontWeight: 500
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: officeColor,
                flexShrink: 0
              }}
            />
            <Typography
              variant='caption'
              sx={{
                fontSize: '0.65rem',
                fontWeight: 500,
                color: 'text.primary',
                lineHeight: 1
              }}
              title={`${booking.officeName} - ${booking.deskName}`}
            >
              {booking.bookerName}
            </Typography>
          </Box>
        )
      })}
      {!expanded && remainingCount > 0 && shouldTruncate && (
        <Chip
          label={`+${remainingCount} more`}
          size='small'
          onClick={e => {
            e.stopPropagation()
            setExpanded(true)
          }}
          className='booking-expand-chip'
          sx={{
            height: 'auto',
            py: 0.25,
            px: 0.5,
            fontSize: '0.65rem',
            fontWeight: 500,
            cursor: 'pointer',
            '& .MuiChip-label': {
              px: 0.5,
              py: 0
            }
          }}
        />
      )}
    </Box>
  )
}
